import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  input,
  output,
  OnDestroy,
  OnInit,
  effect,
} from '@angular/core';
import { MessageCenterService } from 'src/app/modules/core/services/message-center.service';
import { BRAND } from '~environments/environment';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
@Component({
  selector: 'main-nav-message-center',
  templateUrl: './main-nav-message-center.component.html',
  styleUrls: ['./main-nav-message-center.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MainNavMessageCenterComponent
  implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private resize$ = new Subject<void>();
  private scroll$ = new Subject<void>();
  private readonly TRIANGLE_ARROW_OFFSET = 8;

  public unseenNotificationSub: Subscription;

  // Inputs
  public readonly isProfileNav = input(false);
  public readonly messageCenterIcon = input(false);
  public readonly myMessagesText = input('My messages');
  public readonly hideMessageCenterContainer = input(false);
  public readonly isMobile = input(false);

  // Outputs
  public readonly messageCenterStatus = output<{ newMessages: boolean }>();
  public readonly displayBadgeOnProfile = output<boolean>();

  public menuOpen = false;
  public areNewMessages = false;
  public unReadMessagesCount = '';

  constructor(
    private messageCenterService: MessageCenterService,
    private cdr: ChangeDetectorRef,
  ) {
    effect(() => {
      if (this.hideMessageCenterContainer() && this.menuOpen) {
        this.hideMessageCenter();
      }
    });

    // Debounce window resize events
    this.resize$
      .pipe(
        debounceTime(150), // Wait 150ms after resize stops
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.handleMobileScrollBehavior();
        this.positionMessageCenter();
        this.cdr.markForCheck();
      });
    // Debounce scroll events
    this.scroll$
      .pipe(
        debounceTime(150), // Debounce scroll for performance
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.handleScrollEvent();
      });

  }

  ngOnInit(): void {
    this.unseenNotificationSub = this.getUnseenNotificationCount();
    window.addEventListener(
      'badgeStatusUpdate',
      this.handleBadgeUpdate as EventListener
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.unseenNotificationSub) {
      this.unseenNotificationSub.unsubscribe();
    }
    window.removeEventListener(
      'badgeStatusUpdate',
      this.handleBadgeUpdate as EventListener
    );
  }

  @HostListener('window:resize')
  onResize() {
    this.resize$.next();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    if (
      !target.closest('#item-Message-Center') &&
      !target.closest('.message-center-icon') &&
      !target.closest('.message-center-trigger') &&
      !target.closest('.message-center-container__dropdown') &&
      !target.closest('.message-center-container-box')
    ) {
      this.hideMessageCenter();
    }
  }

  @HostListener('window:scroll')
  onScroll(): void {
    this.scroll$.next();
  }

  private handleScrollEvent(): void {
    if (!this.messageCenterIcon()) {
      return;
    }

    const messageCenterEntry = document.getElementById('message-center-entry');
    const messageCenterContainerBox = document.getElementById('message-center-container-box');
    if (!messageCenterEntry || !messageCenterContainerBox) {
      return;
    }

    const rect = messageCenterEntry.getBoundingClientRect();
    const isOutOfView = rect.bottom < 0 || rect.top > window.innerHeight;

    if (isOutOfView) {
      this.hideMessageCenter();
    }
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
    this.handleMobileScrollBehavior();
    this.messageCenterService.updateMenuOpen(this.menuOpen);
    this.messageCenterStatus.emit({
      newMessages: this.areNewMessages,
    });
    if (this.menuOpen) {
      this.positionMessageCenter();
    }

  }

  getUnseenNotificationCount() {
    return this.messageCenterService
      .getUnseenNotificationCount()
      .subscribe(({ hasNewMessages, displayCount }) => {
        this.areNewMessages = hasNewMessages;
        this.unReadMessagesCount = displayCount;
        this.displayBadgeOnProfile.emit(this.areNewMessages);
        this.cdr.markForCheck();
      });
  }

  hideMessageCenter() {
    this.menuOpen = false;
    this.handleMobileScrollBehavior();
    this.messageCenterService.updateMenuOpen(this.menuOpen);
    this.cdr.markForCheck();
  }

  handleBadgeUpdate = (event: Event) => {
    const customEvent = event as CustomEvent;
    const { hasNewMessages, displayCount } = customEvent.detail;
    this.areNewMessages = hasNewMessages;
    this.unReadMessagesCount = displayCount;
    this.cdr.markForCheck();
    this.displayBadgeOnProfile.emit(this.areNewMessages);
  };

  getMessageCenterIcon(): string {
    const brand = BRAND;
    const suffix = this.areNewMessages ? '-new' : '';
    return `assets/images/${brand}/message-center${suffix}.svg`;
  }
  private positionMessageCenter(): void {
    const messageCenterContainerBox = document.getElementById('message-center-container-box');
    if (!messageCenterContainerBox) {
      return;
    };
    this.setPopupHeight();
    if (!this.isMobile()) {
      this.positionDesktopDropdown(messageCenterContainerBox);
    }
  }

  private positionDesktopDropdown(messageCenterContainerBox: HTMLElement): void {
    const messageCenterEntry = document.getElementById('message-center-entry');
    const entryTriangle = document.getElementById('message-center-container-box__triangle');
    const navInner = document.getElementsByClassName('rcl-header-inner');

    if (!messageCenterEntry || !entryTriangle || !navInner) {
      return;
    }
    const entryRect = messageCenterEntry.getBoundingClientRect();
    const navInnerElement = navInner[0] as HTMLElement;
    if (!navInnerElement) {
      return;
    }

    const navInnerRect = navInnerElement.getBoundingClientRect();

    // Position the dropdown box aligned with the right edge of nav-inner
    const distanceFromRight = window.innerWidth - navInnerRect.right - this.getScrollbarWidth();

    messageCenterContainerBox.style.right = `${distanceFromRight}px`;

    // Calculate triangle position to point at the center of the message center entry
    const entryStyle = window.getComputedStyle(messageCenterEntry);
    const marginLeft = parseFloat(entryStyle.marginLeft) || 0;
    const marginRight = parseFloat(entryStyle.marginRight) || 0;
    const totalWidth = entryRect.width + marginLeft + marginRight;

    // Find the center of the entry element from the viewport's right edge
    const entryCenterFromViewportRight = window.innerWidth - (entryRect.right + marginRight) - this.getScrollbarWidth();

    // Position triangle to align with entry center, accounting for triangle arrow width
    const triangleRightPosition = (entryCenterFromViewportRight - distanceFromRight) + (totalWidth / 2) - this.TRIANGLE_ARROW_OFFSET;
    entryTriangle.style.right = `${triangleRightPosition}px`;
  }

  getScrollbarWidth() {
    return (window.innerWidth - document.documentElement.clientWidth);
  }

  setPopupHeight() {
    const navInner = document.querySelector('.rcl-header-inner') as HTMLElement;
    if (!navInner) {
      return;
    }
    const rect = navInner.getBoundingClientRect();
    const headerBottom = rect.bottom;
    const distanceFromBottom = this.isMobile() ? 0 : 20;
    // Height from bottom of header to bottom of viewport
    const popupHeight = `calc(100vh - ${headerBottom + distanceFromBottom}px)`;
    document.documentElement.style.setProperty(
      '--message-center-height',
      popupHeight
    );

  }
  private handleMobileScrollBehavior(): void {
    document.body.style.overflow = this.menuOpen && this.isMobile() ? 'hidden' : '';
  }
}
